import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { ArrowRight, TrainFront, Clock3, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal, MaskedLines } from "@/components/motion";
import { usePageMeta } from "@/hooks/usePageMeta";

const Dintorni = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: t.seo.dintorni.title, description: t.seo.dintorni.description });
  const d = t.dintorni;

  return (
    <div data-testid="dintorni-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40">
        <Reveal y={20}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{d.overline}</p>
        </Reveal>
        <h1 className="font-display font-medium text-5xl md:text-7xl tracking-tight mt-4" data-testid="dintorni-title">
          <MaskedLines lines={[d.title]} delay={0.05} />
        </h1>
        <Reveal delay={0.15}>
          <p className="mt-7 max-w-2xl text-stone text-base md:text-lg leading-relaxed font-light" data-testid="dintorni-sub">
            {d.sub}
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-linen px-5 py-2.5 text-sm text-stone">
            <TrainFront className="h-4 w-4 text-terracotta" />
            <span data-testid="dintorni-station-note">Postioma · {d.stationNote}</span>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20 md:mt-28 space-y-24 md:space-y-36">
        {d.places.map((p, i) => (
          <div key={p.name} className={`grid md:grid-cols-12 gap-10 items-center`} data-testid={`place-${i}`}>
            <Reveal className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
              <div className="overflow-hidden rounded-2xl aspect-[16/10] group">
                <img
                  src={`/images/dintorni/${p.image.file}`}
                  alt={p.image.alt[lang] || p.image.alt.it}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12} className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1 md:text-right" : ""}`}>
              <span className="inline-flex items-center gap-2 rounded-full bg-sand px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-terracotta-dark">
                <Clock3 className="h-3.5 w-3.5" />
                {p.time}
              </span>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-5">{p.name}</h2>
              <p className={`mt-5 text-stone leading-relaxed font-light max-w-md ${i % 2 === 1 ? "md:ml-auto" : ""}`}>{p.text}</p>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-36" data-testid="dintorni-practical">
        <Reveal>
          <div className="rounded-[2rem] bg-sand px-8 md:px-16 py-14 md:py-20">
            <h2 className="font-display text-3xl md:text-5xl tracking-tight">{d.practicalTitle}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6 mt-10">
              {d.practical.map((item, i) => (
                <Reveal key={i} delay={i * 0.05} y={14}>
                  <div className="flex items-center gap-3 text-ink/80">
                    <MapPin className="h-4 w-4 text-olive shrink-0" />
                    <span>{item}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32" data-testid="dintorni-cta">
        <Reveal>
          <div className="rounded-[2rem] bg-terracotta text-cream px-8 md:px-16 py-14 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight max-w-xl">{d.cta}</h2>
            <Link
              to="/prenota"
              data-testid="dintorni-book-button"
              className="group shrink-0 self-start md:self-auto inline-flex items-center gap-3 rounded-full bg-cream text-ink px-8 py-4 font-medium hover:bg-sand transition-colors duration-300"
            >
              {t.nav.book}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Dintorni;
